import { createContext, useContext, useState, useEffect, useMemo } from 'react'
import { loadAllData, getUniqueValues } from '../services/dataLoader'

const DataContext = createContext()

export function DataProvider({ children }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadAllData()
      .then(setData)
      .catch(err => {
        console.error('Failed to load data:', err)
        setError(err.message)
      })
      .finally(() => setLoading(false))
  }, [])

  const computed = useMemo(() => {
    if (!data) return null

    const { customers, monthlyArr, arrMovements, deals, financialTargets, onboarding } = data

    // Get unique filter options
    const countries = getUniqueValues(customers, 'country')
    const subscriptionTypes = getUniqueValues(customers, 'subscription_type')
    const accountManagers = getUniqueValues(customers, 'account_manager')
    const months = getUniqueValues(monthlyArr, 'month').sort()

    // Active customers
    const activeCustomers = customers.filter(c => c.status === 'active')

    // ARR by month (total)
    const arrByMonth = monthlyArr.reduce((acc, record) => {
      const { month, arr_value } = record
      acc[month] = (acc[month] || 0) + arr_value
      return acc
    }, {})

    // Get latest month
    const latestMonth = months[months.length - 1]
    const previousMonth = months[months.length - 2]

    // Current total ARR
    const currentTotalArr = arrByMonth[latestMonth] || 0
    const previousTotalArr = arrByMonth[previousMonth] || 0

    // ARR by country for latest month
    const arrByCountry = monthlyArr
      .filter(r => r.month === latestMonth)
      .reduce((acc, record) => {
        const { country, arr_value } = record
        acc[country] = (acc[country] || 0) + arr_value
        return acc
      }, {})

    // ARR by subscription type for latest month
    const arrBySubscription = monthlyArr
      .filter(r => r.month === latestMonth)
      .reduce((acc, record) => {
        const { subscription_type, arr_value } = record
        acc[subscription_type] = (acc[subscription_type] || 0) + arr_value
        return acc
      }, {})

    // Movements by type for latest month
    const movementsByType = arrMovements
      .filter(m => m.month === latestMonth)
      .reduce((acc, record) => {
        const { movement_type, arr_change } = record
        acc[movement_type] = (acc[movement_type] || 0) + arr_change
        return acc
      }, {})

    // Deals by stage
    const dealsByStage = deals.reduce((acc, deal) => {
      const stage = deal.current_stage
      if (!acc[stage]) acc[stage] = { count: 0, value: 0 }
      acc[stage].count++
      acc[stage].value += deal.deal_value || 0
      return acc
    }, {})

    // Onboarding stats
    const onboardingActive = onboarding.filter(o =>
      o.current_stage === true && o.onboarding_stage !== 'Done'
    )

    return {
      countries,
      subscriptionTypes,
      accountManagers,
      months,
      latestMonth,
      previousMonth,
      activeCustomers,
      arrByMonth,
      arrByCountry,
      arrBySubscription,
      movementsByType,
      dealsByStage,
      onboardingActive,
      currentTotalArr,
      previousTotalArr
    }
  }, [data])

  const value = {
    data,
    computed,
    loading,
    error
  }

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  )
}

export function useData() {
  const context = useContext(DataContext)
  if (!context) {
    throw new Error('useData must be used within a DataProvider')
  }
  return context
}
